import React, { useState, useContext, useRef } from 'react';
import { Link } from 'react-router-dom';

import { formatImageUrl, isDev, isProduction } from 'helpers';

import { Menu, MenuItems } from '../Menu';
import { Locale } from '../Locale';
import { useOnClickOutside } from '../../utils/main';
import { Button } from '../Button';
import { Icon } from '../Icons';
import { StoreContext } from 'store';

import { Text } from '../Text';
import { useAuth } from '../ContextAuth';
import { useModals } from '../ContextModals';

import './headers.scss';
import { api } from 'api';

const getUserAvatarUrl = (avatarId: string) =>
  (isDev() ? 'http://d3lzmipwt8uk2x.cloudfront.net' : process.env.PUBLIC_URL || '') +
  (avatarId ? `/assets/avatars/${avatarId}.png` : null);

interface HeaderProps {
  hideProfileInfo?: boolean;
}

export const Header: React.FC<HeaderProps> = ({ hideProfileInfo }) => {
  const { user, login, logout } = useAuth();
  const [isOpened, setIsOpened] = useState(false);
  const subdomain = window.location.host?.split('.')[0];

  const onMenuClick = () => {
    setIsOpened(!isOpened);
  };

  const renderLogo = () => {
    if (isProduction() && subdomain !== 'club') {
      return (
        <div
          className="c-header__logo"
          onClick={() => {
            window.location.replace('https://club.tacticool.game/');
          }}
        >
          <img src={formatImageUrl('/img/logo.svg')} alt="tacticool" />
          <div className="c-header__title">
            PLAYERS
            <br />
            CLUB
          </div>
        </div>
      );
    }

    return (
      <Link className="c-header__logo" to="/">
        <img src={formatImageUrl('/img/logo.svg')} alt="tacticool" />
        <div className="c-header__title">
          PLAYERS
          <br />
          CLUB
        </div>
      </Link>
    );
  };

  return (
    <header className="c-header">
      <div className="container">
        {renderLogo()}
        <MenuItems isOpened={isOpened} />
        <Locale />
        <ProfileInfo
          hide={hideProfileInfo}
          user={user}
          logout={logout}
          login={login}
        />
        <Menu isOpened={isOpened} onClick={onMenuClick} />
      </div>
    </header>
  );
};

interface ProfileInfoProps {
  hide?: boolean;
  user: api.Account | null;
  logout: () => void;
  login: (userData: api.Account) => void;
}

const ProfileInfo: React.FC<ProfileInfoProps> = ({
  hide = true,
  user,
  logout,
  login,
}) => {
  const { translation: t } = useContext(StoreContext);
  const { auth } = useModals();

  const ref = useRef<HTMLDivElement>(null);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  useOnClickOutside(ref, () => setIsTooltipOpen(false));

  if (hide) {
    return null;
  }

  if (!user) {
    return (
      <Text.CapsM
        hasMobileSize={false}
        className="user-info__login"
        onClick={() => {
          auth.open();
          localStorage.setItem('auth_component', 'header');
        }}
      >
        {t.actions.login}
      </Text.CapsM>
    );
  }

  return (
    <div
      ref={ref}
      className="user-info"
      onClick={() => setIsTooltipOpen(!isTooltipOpen)}
    >
      {user?.avatarId && (
        <img
          src={getUserAvatarUrl(`${user.avatarId}`)}
          alt="avatar"
          className="user-info__avatar"
        />
      )}
      <Text.CapsS
        className="user-info__name"
        hasMobileSize={false}
      >{`${user.publicId}`}</Text.CapsS>
      <LogoutTooltip isVisible={isTooltipOpen} logout={logout} />
    </div>
  );
};

interface LogoutTooltipProps {
  logout: () => void;
  isVisible: boolean;
}

const LogoutTooltip: React.FC<LogoutTooltipProps> = ({ logout, isVisible }) => {
  const { translation: t } = useContext(StoreContext);

  return (
    <div style={{ opacity: isVisible ? 1 : 0 }} className="user-info__tooltip">
      <svg
        className="user-info__tooltip-arrow"
        width="8"
        height="5"
        viewBox="0 0 8 5"
        fill="rgba(32, 36, 40, 0.9)"
      >
        <path d="M4 0L0 5H8L4 0Z" stroke="none" />
      </svg>
      <Button
        size="m"
        withTriangle
        Icon={Icon.Close}
        onClick={() => {
          logout();
        }}
        className="user-info__tooltip-button"
      >
        {t.actions.logout}
      </Button>
    </div>
  );
};
